import { FC, useMemo, useState } from 'react'
import clsx from 'clsx'
import { isVideo } from '@common/misc'
import PdImage from '@components/pd-image/pd-image'
import PdMedia from '@components/pd-media/pd-media'
import PdStandaloneVideo from '@components/pd-standalone-video/pd-standalone-video'
import { CustomerCaseNewInfoData } from '@service/customer-new.service'
import Image from 'next/image'
import dynamic from 'next/dynamic'
const CkRichText = dynamic(() => import('@/components/pd-ck-rich-text'), {
  ssr: false,
})

interface Section2Props {
  data: {
    middleInfo?: CustomerCaseNewInfoData['middleInfo']
    cardTitle?: string
    card?: CustomerCaseNewInfoData['card']
  }
}

const Section2: FC<Section2Props> = ({ data }) => {
  const { middleInfo, card, cardTitle = '' } = data
  const [playIcon, setPlayIcon] = useState<string>('https://cdn.pudutech.com/official-website/about-us/btn_play.svg')
  const [playingVideoSrc, setPlayingVideoSrc] = useState<string>('')
  const [playing, setPlaying] = useState<boolean>(false)

  function play(videoSrc: string) {
    setPlayingVideoSrc(videoSrc)
    setPlaying(true)
  }

  const hasMiddleInfoContent = useMemo(() => {
    return (
      middleInfo &&
      (middleInfo.middleTops?.length > 0 ||
        middleInfo.topTitle?.trim() ||
        middleInfo.middleMediaUrl?.trim() ||
        middleInfo.middleBottoms?.[0]?.title?.trim() ||
        middleInfo.middleBottoms?.[0]?.desc?.trim())
    )
  }, [middleInfo])

  const hasCardContent = useMemo(() => {
    return card && (card.title?.trim() || card.content?.trim() || card.secondTitle?.trim() || card.mediaUrl?.trim())
  }, [card])

  if (!hasMiddleInfoContent && !hasCardContent) {
    return null
  }
  return (
    <div className={clsx('w-full bg-pdbg py-15')}>
      <div className="max-w-250 mx-auto px-6 laptop:px-0">
        {hasMiddleInfoContent && (
          <>
            {middleInfo?.topTitle && (
              <h1 className="text-center text-black/90 text-2xl laptop:text-42 font-medium mb-8 laptop:mb-16">
                {middleInfo?.topTitle}
              </h1>
            )}
            <div className="bg-white p-6 laptop:p-12 flex flex-col gap-y-16">
              {middleInfo?.middleTops && middleInfo?.middleTops.length > 0 && (
                <ul className="grid grid-cols-2 laptop:flex laptop:justify-between gap-x-6">
                  {middleInfo?.middleTops.map((item) => (
                    <li key={item.title} className="flex flex-col gap-y-2 flex-1">
                      <h2 className="text-blue-600 text-xl font-semibold">{item.title}</h2>
                      <p className="text-black/90 text-sm font-normal">{item.desc}</p>
                    </li>
                  ))}
                </ul>
              )}
              {middleInfo?.middleMediaUrl && (
                <div className="w-full h-368px laptop:h-621px">
                  <PdMedia
                    src={middleInfo?.middleMediaUrl}
                    mask={isVideo(middleInfo?.middleMediaUrl)}
                    stopAutoplay={true}
                  >
                    {isVideo(middleInfo?.middleMediaUrl) && (
                      <img
                        src={playIcon}
                        className="absolute block w-12 h-12 transition-all hover:scale-116 left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 cursor-pointer"
                        alt="play"
                        onMouseEnter={() =>
                          setPlayIcon('https://cdn.pudutech.com/official-website/about-us/btn_play_hover.svg')
                        }
                        onMouseLeave={() =>
                          setPlayIcon('https://cdn.pudutech.com/official-website/about-us/btn_play.svg')
                        }
                        onClick={() => play(middleInfo?.middleMediaUrl)}
                      />
                    )}
                  </PdMedia>
                </div>
              )}
              <div className="w-full">
                {/* Background */}
                {middleInfo?.middleBottoms &&
                  middleInfo?.middleBottoms.length > 0 &&
                  middleInfo?.middleBottoms.map((item) => (
                    <div
                      className="pb-6 bg-white inline-flex flex-col justify-start items-start gap-4 w-full"
                      key={item.title}
                    >
                      <div className="inline-flex flex-col justify-start items-start gap-6">
                        {item.title && <div className="text-black/90 text-xl font-semibold">{item.title}</div>}
                        {item.desc && <CkRichText richText={item.desc} className="[&>p]:!bg-white" />}
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </>
        )}
        {/* Testimonial */}
        {card?.content && card?.content.length > 0 && (
          <>
            <h1
              className={clsx(
                'text-center text-black/90 text-2xl laptop:text-42 font-medium mb-6 laptop:mb-12',
                hasMiddleInfoContent && 'mt-6 laptop:mt-12'
              )}
            >
              {cardTitle}
            </h1>
            <div className="flex flex-col laptop:flex-row bg-white">
              <div className="w-full laptop:w-125" style={{ aspectRatio: '3/2' }}>
                <PdImage src={card.mediaUrl} enableMask={false} object="cover" width={'100%'} height={'100%'} />
              </div>
              <div className="flex-1 flex flex-col gap-y-2 p-6 w-full relative">
                <div className="w-5 h-5 laptop:w-10 laptop:h-10 self-end absolute">
                  <Image src="/icons/icon_quote.svg" alt="" layout="fill" />
                </div>
                <h2 className="text-black/90 text-xl laptop:text-2xl font-semibold w-[calc(100%-1.25rem)] laptop:w-[calc(100%-2.5rem)]">
                  {card.title}
                </h2>
                <p className="text-black/60 text-sm font-normal leading-snug">{card.content}</p>
                <h3 className="text-black/60 text-sm font-semibold leading-snug">{card.secondTitle}</h3>
              </div>
            </div>
          </>
        )}
      </div>
      <PdStandaloneVideo source={playingVideoSrc} show={playing} onClosed={() => setPlaying(false)} />
    </div>
  )
}

export default Section2
